// src/components/UI/ChartWrapper.js
import React, { useState, useRef } from 'react';
import { Card, Spinner, Alert, Button, Dropdown, ButtonGroup } from 'react-bootstrap';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';

// Registrace potřebných Chart.js komponent
Chart.register(...registerables);

/**
 * Wrapper komponenta pro grafy s možnostmi exportu
 */
const ChartWrapper = ({
                          type = 'line',
                          data,
                          options,
                          title,
                          height = 300,
                          loading = false,
                          error = null,
                          emptyMessage = 'Nejsou k dispozici žádná data pro zobrazení grafu',
                          exportOptions = {
                              csv: true,
                              png: true,
                              pdf: false
                          },
                          onRefresh = null,
                          onExport = null,
                          timeRanges = null,
                          selectedTimeRange = null,
                          onTimeRangeChange = null
                      }) => {
    const [chartInstance, setChartInstance] = useState(null);
    const chartRef = useRef(null);

    // Určení typu grafu
    let dataSet = JSON.parse(JSON.stringify(data));
    if (dataSet?.datasets.length > 0) {
        dataSet.datasets?.map((d, i)=> {
            dataSet.datasets[i].data = Array.from(d.data).reverse();
        });

        dataSet.labels = Array.from(dataSet.labels).reverse();
    }
    console.log(dataSet)
    const renderChart = () => {
        switch (type.toLowerCase()) {
            case 'bar':
                return <Bar ref={chartRef} data={dataSet} options={options} />;
            case 'pie':
                return <Pie ref={chartRef} data={dataSet} options={options} />;
            case 'doughnut':
                return <Doughnut ref={chartRef} data={dataSet} options={options} />;
            case 'line':
            default:
                return <Line ref={chartRef} data={dataSet} options={options} />;
        }
    };

    // Uložení reference na instanci grafu při montování komponenty
    const saveChartInstance = (instance) => {
        if (instance) {
            setChartInstance(instance);
        }
    };

    // Export grafu do PNG
    const handleExportToPNG = () => {
        if (!chartRef.current) return;

        const canvas = chartRef.current.canvas;
        if (!canvas) return;

        const image = canvas.toDataURL('image/png', 1.0);

        // Vytvoření a stažení souboru
        const link = document.createElement('a');
        link.href = image;
        link.download = `${title || 'chart'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Zpracování exportu
    const handleExport = (type) => {
        if (onExport) {
            onExport(type);
        } else if (type === 'png') {
            handleExportToPNG();
        }
    };

    // Zpracování změny časového rozsahu
    const handleTimeRangeChange = (range) => {
        if (onTimeRangeChange) {
            onTimeRangeChange(range);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="m-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
            </Alert>
        );
    }

    if (!data || !data.datasets || data.datasets.length === 0) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center text-muted" style={{ height: `${height}px` }}>
                <i className="fas fa-chart-area fa-3x mb-3"></i>
                <p>{emptyMessage}</p>
            </div>
        );
    }

    return (
        <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{title}</h5>

                <div className="d-flex">
                    {/* Výběr časového rozsahu */}
                    {timeRanges && onTimeRangeChange && (
                        <ButtonGroup className="me-2">
                            {timeRanges.map(range => (
                                <Button
                                    key={range.value}
                                    variant={selectedTimeRange === range.value ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => handleTimeRangeChange(range.value)}
                                >
                                    {range.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    )}

                    {/* Dropdown pro export */}
                    {(exportOptions.csv || exportOptions.png || exportOptions.pdf) && (
                        <Dropdown className="me-2">
                            <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                id="chart-export-dropdown"
                            >
                                <i className="fas fa-download me-1"></i> Export
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {exportOptions.csv && (
                                    <Dropdown.Item onClick={() => handleExport('csv')}>
                                        <i className="fas fa-file-csv me-2"></i> Export CSV
                                    </Dropdown.Item>
                                )}
                                {exportOptions.png && (
                                    <Dropdown.Item onClick={() => handleExport('png')}>
                                        <i className="fas fa-file-image me-2"></i> Export PNG
                                    </Dropdown.Item>
                                )}
                                {exportOptions.pdf && (
                                    <Dropdown.Item onClick={() => handleExport('pdf')}>
                                        <i className="fas fa-file-pdf me-2"></i> Export PDF
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}

                    {/* Tlačítko pro obnovení */}
                    {onRefresh && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={onRefresh}
                            title="Obnovit data"
                        >
                            <i className="fas fa-sync-alt"></i>
                        </Button>
                    )}
                </div>
            </Card.Header>

            <Card.Body>
                <div style={{ height: `${height}px` }}>
                    {renderChart()}
                </div>
            </Card.Body>
        </Card>
    );
};

export default ChartWrapper;